import { useSuspenseQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import sumBy from 'lodash/sumBy';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Form,
  FormField,
  FormItem,
  FormMessage,
  Heading,
  Icon,
  Input,
  Stack,
  Textarea,
  useToast,
} from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';

import { ArtifactSearch, DataSetItem } from './ArtifactSearch';
import { useWorkflow } from './DuplicateWorkflowContext';
import {
  FactVerificationTargetConfigsForm,
  TargetConfig,
} from './FactVerificationTargetConfigsForm';
import { ModelPromptTemplateSearch } from './ModelPromptTemplateSearch';
import { WorkflowMatrixDetails } from './WorkflowMatrixDetails';
import { getWorkflowOptionsQuery } from './eval.operations';

const createWokflowFormSchema = z.object({
  name: z.string().min(3),
  description: z.string().optional(),
});

type FormData = z.infer<typeof createWokflowFormSchema>;

export const CreateWorkflowPage: React.FC = () => {
  const logger = useNamedLogger('create-workflow-page');
  useSuspenseQuery(getWorkflowOptionsQuery);
  const toast = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(createWokflowFormSchema),
  });

  const navigate = useNavigate();

  const [
    factVerifierModelPromptTemplateId,
    setFactVerifierModelPromptTemplateId,
  ] = useState<string | undefined>(undefined);

  const [selectedTargetConfigs, setSelectedTargetConfigs] = useState<
    TargetConfig[]
  >([]);

  const [selectedDataSetItems, setSelectedDataSetItems] = useState<
    DataSetItem[]
  >([]);

  const totalArtifacts = sumBy(selectedDataSetItems, (a) => a.totalArtifacts);
  const totalFacts = sumBy(selectedDataSetItems, (a) => a.totalFacts);

  const artifactsAndModelSelectionValid =
    selectedDataSetItems.length > 0 && selectedTargetConfigs.length > 0;

  const client = useApiClient();

  const onSubmit = async ({ name, description }: FormData) => {
    if (
      !artifactsAndModelSelectionValid ||
      !factVerifierModelPromptTemplateId
    ) {
      return;
    }

    try {
      const response = await client.eval.startCustomFactVerification({
        body: {
          name,
          description,
          dataSetIds: selectedDataSetItems.map((d) => d.id),
          targetGeneratorConfigs: selectedTargetConfigs.map((c) => ({
            modelPromptTemplateId: c.modelPromptTemplateId,
            outputTemplateId: c.outputTemplateId,
          })),
          factVerifierModelPromptTemplateId,
        },
      });

      if (response.status !== 201) {
        toast.error('Failed to create workflow');
      } else {
        const { workflowId } = response.body;
        toast.success('Workflow created successfully');
        navigate(`/admin/fact-verification/${workflowId}`);
      }
    } catch (e) {
      toast.error('Failed to create workflow');
    }
  };

  // Consume DuplicateWorkflowContext values as defaults if available
  const workflowContext = useWorkflow();
  useEffect(() => {
    if (workflowContext.targetConfigs.length > 0) {
      setSelectedTargetConfigs(workflowContext.targetConfigs);
    }
    if (workflowContext.dataSets.length > 0) {
      setSelectedDataSetItems(workflowContext.dataSets);
    }
  }, [workflowContext]);

  return (
    <div className="space-y-3">
      <div className="my-2 py-2">
        <Heading>Fact Verification</Heading>
        <Heading size="2xl">
          Select the model prompt templates and artifacts for this workflow
        </Heading>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit, () => {
            logger.debug('Invalid form submission attempted');
          })}
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="max-w-lg">
                <label htmlFor="name" className="sr-only">
                  Name
                </label>
                <Input className="bg-white/5" placeholder="Name" {...field} />
                {error && (
                  <FormMessage className="mt-4">{error.message}</FormMessage>
                )}
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState: { error } }) => (
              <FormItem className="max-w-lg">
                <label htmlFor="description" className="sr-only">
                  Description
                </label>
                <Textarea
                  className="bg-white/5"
                  placeholder="Description"
                  {...field}
                />
                {error && (
                  <FormMessage className="mt-4">{error.message}</FormMessage>
                )}
              </FormItem>
            )}
          />
          <Button
            className="mt-4"
            disabled={
              !artifactsAndModelSelectionValid ||
              !factVerifierModelPromptTemplateId
            }
          >
            Run Workflow
          </Button>
        </form>
      </Form>

      <Stack gap={8}>
        <Stack gap={3}>
          <Heading size="2xl" weight="semibold">
            Fact Verifier
          </Heading>
          {!factVerifierModelPromptTemplateId && (
            <Alert variant="warning">
              <Icon name="Info" size="xl" />
              <AlertTitle>Invalid Selection</AlertTitle>
              <AlertDescription>
                Choose the Fact Verification Model Prompt template that will be
                used for this workflow
              </AlertDescription>
            </Alert>
          )}
          <ModelPromptTemplateSearch
            isSingleSelection
            onSelectionChanged={([id]) =>
              setFactVerifierModelPromptTemplateId(id)
            }
            selectedIds={
              factVerifierModelPromptTemplateId
                ? [factVerifierModelPromptTemplateId]
                : []
            }
            filter={(pt) => pt.template.outputType === 'FACT_VERIFICATION'}
          />
        </Stack>

        <div className="h-px w-full bg-bg-positive-strong-primary" />
        <Stack gap={3}>
          <Heading size="2xl" weight="semibold">
            Configs Being Evaluated
          </Heading>
          {match(artifactsAndModelSelectionValid)
            .with(true, () => (
              <Alert>
                <Icon name="Info" size="xl" />
                <AlertTitle>Selection Details</AlertTitle>
                <AlertDescription className="space-y-1">
                  <WorkflowMatrixDetails
                    modelPromptTemplatesCount={selectedTargetConfigs.length}
                    totalArtifacts={totalArtifacts}
                    totalFacts={totalFacts}
                  />
                </AlertDescription>
              </Alert>
            ))
            .otherwise(() => (
              <Alert variant="warning">
                <Icon name="Info" size="xl" />
                <AlertTitle>Invalid Selection</AlertTitle>
                <AlertDescription>
                  Select at least one Target Config and at least one Artifact
                  Dataset
                </AlertDescription>
              </Alert>
            ))}
        </Stack>
        <Stack gap={3}>
          <Heading size="2xl" weight="semibold">
            Select Dataset
          </Heading>
          <ArtifactSearch
            selectedDataSetItems={selectedDataSetItems}
            onSelectionChanged={(items) => setSelectedDataSetItems(items)}
          />
        </Stack>
        <Stack gap={3}>
          <Heading size="2xl" weight="semibold">
            Select Target Configs
          </Heading>
          <FactVerificationTargetConfigsForm
            selectedConfigs={selectedTargetConfigs}
            onSelectionChanged={(configs) => setSelectedTargetConfigs(configs)}
          />
        </Stack>
      </Stack>
    </div>
  );
};
